/**
 * Goal fireworks — shells that launch, rise, and burst.
 *
 * A single expanding ball of sparks is a grenade, not a firework. What makes a
 * display read as a display is the STRUCTURE: a shell you can watch climb, a
 * beat of nothing at the apex, then a burst that has a shape. So this is a
 * two-stage system — a small set of rising shells, each carrying a burst plan —
 * rather than an emitter that sprays.
 *
 * DETERMINISM. Same rule as ./effects, and for the same reason: the display
 * plays during the replay, so a display that cannot be reproduced is a replay
 * that shows something different every time. Every number below is drawn from a
 * seeded `Rng` handed in by the caller, and the two things a test can pin — the
 * shell plan and the initial state of every burst particle — are exported as
 * PURE functions of that generator.
 *
 * BUDGET. Everything goes into the existing `ParticlePool`, which is one
 * `Points` draw call for the whole arena's worth of effects. No mesh is created
 * per spark. The only meshes this file owns are the burst glows, and there are
 * `GLOW_POOL` of them, allocated once and reused, sharing one sphere geometry.
 * See `PARTICLE_BUDGET` for the counts.
 *
 * FRAME. Positions here are in the THREE frame (+Y up, z = -physics.y), because
 * that is what `ParticlePool` and the scene graph take. The only physics-frame
 * quantity that crosses the boundary is `goalSign` — which end the ball went in
 * — and it is converted once, in `planFireworks`.
 */

import {
  AdditiveBlending,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import { TEAM_COLORS } from './materials';
import type { ParticlePool, Rng } from './effects';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export type BurstKind =
  /** Classic sphere: even shell, bright core, moderate hang. */
  | 'peony'
  /** Slow, heavy, long-lived: the sparks drip and fade on the way down. */
  | 'willow'
  /** Fast, tight, short-lived, flickering. The one you hear. */
  | 'crackle';

/** One shell, fully decided before the show starts. THREE frame. */
export interface ShellPlan {
  /** Seconds after the show begins that this shell leaves the ground. */
  launch: number;
  /** Seconds spent climbing. */
  rise: number;
  kind: BurstKind;
  fromX: number;
  fromY: number;
  fromZ: number;
  apexX: number;
  apexY: number;
  apexZ: number;
  /** Burst size multiplier, 0.7 .. 1.3. */
  scale: number;
  /** True for a white/gold shell rather than a team-coloured one. */
  accent: boolean;
}

/**
 * The initial state of one burst spark. Everything `ParticlePool.spawn` needs
 * except the resolved colour, which depends on the scoring team and so is not
 * part of what the seed decides.
 */
export interface BurstParticle {
  px: number;
  py: number;
  pz: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  size: number;
  sizeGrowth: number;
  gravity: number;
  drag: number;
  /** 0 = shell colour, 1 = white core, 2 = gold accent. */
  tint: number;
  /** Index into the flicker ramp table, or -1 for a flat tint. */
  ramp: number;
}

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

const TUNING = {
  /** Shells per goal, and how far apart they leave the ground. */
  shellCount: 10,
  shellGap: 0.3,
  shellJitter: 0.16,
  /** First shell waits this long, so the ball's own explosion reads as the cue. */
  leadIn: 0.28,

  /** Where shells stand, THREE frame. Depth is measured from halfway to the
   *  goal line; see `planFireworks` for why they are not behind the net. */
  spreadX: 3200,
  depthMin: 2600,
  depthMax: 4700,
  groundY: 60,
  apexMin: 1250,
  apexMax: 1900,
  riseMin: 0.75,
  riseMax: 1.1,

  /** Rising trail: particles per second, and how long each hangs. */
  trailRate: 74,
  trailLifeMin: 0.3,
  trailLifeMax: 0.62,

  /** Burst sizes. */
  peonyCount: 260,
  peonyCore: 60,
  willowCount: 210,
  crackleCount: 170,
  crackleCore: 120,

  /** Glow: how long a burst lights the arena, and how big the flash is. */
  glowLife: 0.55,
  glowRadius: 380,
  /** Pool size. More concurrent bursts than this simply share the newest glows. */
  glowPool: 8,
  /** How much of the arena wash one burst contributes, 0..1. */
  glowWash: 0.55,
} as const;

/**
 * Worst-case spawns for one goal's display, as the budget the pool is sized
 * against. Trails are counted at the longest rise; bursts at the biggest shape,
 * which is the peony at 320 sparks (willow 210, crackle 290).
 *
 *   trails  10 shells * 74/s * 1.1 s   =   820
 *   bursts  10 shells * 320 sparks     = 3,200
 *
 * Nothing like all of it is alive at once: the shells are spread over 3.2 s and
 * the longest spark lives 3.2 s, so the measured peak is around 2,000 — which
 * sits alongside the ball's own 1,400-particle explosion, inside the app's
 * 8,000 pool, with room left for six cars' boost trails.
 */
export const PARTICLE_BUDGET = {
  trails: TUNING.shellCount * Math.ceil(TUNING.trailRate * TUNING.riseMax),
  bursts:
    TUNING.shellCount *
    Math.max(
      TUNING.peonyCount + TUNING.peonyCore,
      TUNING.willowCount,
      TUNING.crackleCount + TUNING.crackleCore
    ),
} as const;

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

const WHITE: [number, number, number] = [1, 0.97, 0.9];
const GOLD: [number, number, number] = [1, 0.78, 0.32];

/**
 * Flicker ramps for the crackle burst, pre-built at eight phases.
 *
 * A ramp only sees the particle's normalized age, so a single shared flicker
 * would strobe every spark in the burst in perfect unison — which does not look
 * like a crackle, it looks like a fault. Eight phases, chosen per particle from
 * the seeded generator, desynchronize it without allocating a closure per spark.
 */
const CRACKLE_RAMPS: readonly ((t: number) => [number, number, number])[] = Array.from(
  { length: 8 },
  (_, i) => {
    const phase = (i / 8) * Math.PI * 2;
    return (t: number): [number, number, number] => {
      const flicker = 0.28 + 0.72 * (0.5 + 0.5 * Math.sin(t * 46 + phase));
      return [flicker, flicker * 0.88, flicker * 0.62];
    };
  }
);

/** Peony: white-hot for the first instant, then the shell's own colour. */
const peonyRamp = (shell: [number, number, number]) => (t: number): [number, number, number] => {
  const k = t < 0.18 ? t / 0.18 : 1;
  return [
    WHITE[0] + (shell[0] - WHITE[0]) * k,
    WHITE[1] + (shell[1] - WHITE[1]) * k,
    WHITE[2] + (shell[2] - WHITE[2]) * k,
  ];
};

/** Willow: gold that cools through amber to a dull ember as it falls. */
const willowRamp = (t: number): [number, number, number] => {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return [1 - 0.35 * k, 0.78 - 0.55 * k, 0.32 - 0.28 * k];
};

// ---------------------------------------------------------------------------
// Pure planners
// ---------------------------------------------------------------------------

/**
 * Lay out one goal's shells.
 *
 * `goalSign` is the PHYSICS y sign of the goal that was scored in (+1 for the
 * orange net at +Y), and the only place the physics frame is read.
 *
 * The shells stand ON the pitch over the scoring half rather than behind the
 * net, which is where a real display would be. Behind the net is outside the
 * arena shell, and whether anything out there is visible depends entirely on
 * what the stadium looks like — a bet this file is not in a position to make.
 * Over the scoring half they are framed by every camera the celebration and the
 * replay use, and they are guaranteed to be on screen.
 */
export const planFireworks = (
  rng: Rng,
  goalSign: number,
  count: number = TUNING.shellCount
): ShellPlan[] => {
  const out: ShellPlan[] = [];
  // THREE z is -physics y, so the scoring end is at negative z for goalSign +1.
  const depthSign = -Math.sign(goalSign || 1);

  for (let i = 0; i < count; i++) {
    const x = rng.range(-TUNING.spreadX, TUNING.spreadX);
    const z = depthSign * rng.range(TUNING.depthMin, TUNING.depthMax);
    const kind: BurstKind = pickKind(rng.next());
    out.push({
      launch: TUNING.leadIn + i * TUNING.shellGap + rng.range(0, TUNING.shellJitter),
      rise: rng.range(TUNING.riseMin, TUNING.riseMax),
      kind,
      fromX: x,
      fromY: TUNING.groundY,
      fromZ: z,
      // The shell drifts a little on the way up; a perfectly vertical mortar
      // reads as an elevator. The depth drift carries `depthSign` so that the
      // two ends of the pitch get the SAME display mirrored, rather than two
      // displays that merely share a seed.
      apexX: x + rng.range(-260, 260),
      apexY: rng.range(TUNING.apexMin, TUNING.apexMax),
      apexZ: z + depthSign * rng.range(-260, 260),
      scale: rng.range(0.75, 1.3),
      accent: rng.next() < 0.3,
    });
  }
  return out;
};

/** 45% peony, 30% willow, 25% crackle. */
const pickKind = (u: number): BurstKind => (u < 0.45 ? 'peony' : u < 0.75 ? 'willow' : 'crackle');

/**
 * Initial states of one burst, in world space around `origin`.
 *
 * Pure and seeded: given the same generator state, kind, origin and scale this
 * returns byte-identical arrays, which is what `test/render/fireworks.test.ts`
 * asserts and what lets a replay show the same display twice.
 *
 * The speed distribution differs per kind on purpose. A peony is close to a
 * uniform shell (`0.55 + 0.45u`) so it reads as a sphere; a willow is slow and
 * heavy so it falls in strands; a crackle is a `u^2` spike, mostly slow with a
 * few sparks thrown far, which is what gives it its ragged edge.
 */
export const burstParticles = (
  rng: Rng,
  kind: BurstKind,
  origin: { x: number; y: number; z: number },
  scale = 1
): BurstParticle[] => {
  const out: BurstParticle[] = [];
  const dir = new Vector3();

  const shell = (
    count: number,
    speedMin: number,
    speedMax: number,
    shape: (u: number) => number,
    life: [number, number],
    size: [number, number],
    sizeGrowth: number,
    gravity: number,
    drag: number,
    tintOf: (u: number) => number,
    ramp: (u: number) => number
  ): void => {
    for (let i = 0; i < count; i++) {
      rng.direction(dir);
      const speed = (speedMin + (speedMax - speedMin) * shape(rng.next())) * scale;
      out.push({
        px: origin.x + dir.x * 12,
        py: origin.y + dir.y * 12,
        pz: origin.z + dir.z * 12,
        vx: dir.x * speed,
        vy: dir.y * speed,
        vz: dir.z * speed,
        life: rng.range(life[0], life[1]),
        size: rng.range(size[0], size[1]),
        sizeGrowth,
        gravity,
        drag,
        tint: tintOf(rng.next()),
        ramp: ramp(rng.next()),
      });
    }
  };

  // The speeds and SIZES below are scaled for an arena 10,000 uu long, viewed
  // from up to 8,000 uu away. Sizes are world units, projected by the pool's
  // shader as `size * uScale / distance`; at this project's FOV `uScale` is
  // around 550, so a 25 uu spark 7,000 uu away is TWO PIXELS. That is what the
  // first build looked like — the glows were visible and the sparks simply were
  // not. A 90 uu spark at the same distance is 7 px, and 30 px from the
  // goal-mouth camera, which is what a firework is supposed to do.
  if (kind === 'peony') {
    // A near-uniform shell, plus a white core that dies first — the core is
    // what stops the burst looking hollow in the first few frames.
    shell(TUNING.peonyCount, 640, 1500, u => 0.55 + 0.45 * u, [1.3, 2.1], [58, 122], 0.35,
      420, 1.15, u => (u < 0.16 ? 2 : 0), () => -1);
    shell(TUNING.peonyCore, 180, 560, u => u, [0.35, 0.7], [86, 170], 0.2,
      240, 2.4, () => 1, () => -1);
    return out;
  }

  if (kind === 'willow') {
    shell(TUNING.willowCount, 340, 800, u => 0.4 + 0.6 * u, [2.2, 3.2], [50, 100], 0.22,
      780, 1.7, u => (u < 0.25 ? 2 : 0), () => -1);
    return out;
  }

  shell(TUNING.crackleCount, 780, 2000, u => u * u, [0.4, 0.95], [42, 80], 0.3,
    380, 3.1, () => 1, u => Math.min(CRACKLE_RAMPS.length - 1, (u * CRACKLE_RAMPS.length) | 0));
  shell(TUNING.crackleCore, 260, 900, u => u, [0.22, 0.5], [36, 66], 0.25,
    340, 3.6, () => 2, () => -1);
  return out;
};

// ---------------------------------------------------------------------------
// The show
// ---------------------------------------------------------------------------

interface ShellState {
  plan: ShellPlan;
  burst: boolean;
  trailCarry: number;
}

interface Glow {
  mesh: Mesh;
  material: MeshBasicMaterial;
  /** Seconds remaining, or 0 for a free slot. */
  life: number;
}

export interface FireworksOptions {
  /** Number of shells per goal. Defaults to the tuned 10. */
  shellCount?: number;
}

export class FireworksShow {
  readonly group = new Group();

  private readonly shells: ShellState[] = [];
  private readonly glows: Glow[] = [];
  private readonly glowGeometry = new SphereGeometry(1, 12, 8);
  private readonly shellCount: number;

  private rng: Rng | null = null;
  private clock = 0;
  private running = false;

  private readonly shellColor: [number, number, number] = [1, 1, 1];
  /** Rebuilt per goal so the pool's per-particle ramp slot shares one function. */
  private peony: (t: number) => [number, number, number] = peonyRamp(WHITE);
  private readonly shellPoint = new Vector3();
  private readonly spawnPoint = new Vector3();

  constructor(
    private readonly particles: ParticlePool,
    options: FireworksOptions = {}
  ) {
    this.shellCount = options.shellCount ?? TUNING.shellCount;
    this.group.name = 'fireworks';
    for (let i = 0; i < TUNING.glowPool; i++) this.glows.push(this.makeGlow());
  }

  /** True while shells are still climbing, bursting or glowing. */
  get active(): boolean {
    return this.running;
  }

  /**
   * 0..1 arena wash from the bursts currently burning, for the HUD to render.
   *
   * A pulsing light would be the honest way to do this, but the arena's lighting
   * rig is not this file's to extend and every extra light in the scene
   * recompiles the material shaders. The wash — a burst-timed tint the HUD lays
   * over the frame — buys most of the read for the cost of one CSS opacity.
   */
  get wash(): number {
    let sum = 0;
    for (const g of this.glows) {
      if (g.life <= 0) continue;
      const k = g.life / TUNING.glowLife;
      sum += TUNING.glowWash * k * k;
    }
    return sum > 1 ? 1 : sum;
  }

  /**
   * Light the fuse.
   *
   * `rng` is the caller's, freshly seeded for this goal — see
   * `EffectsSystem.goalExplosion`. Everything the display does is drawn from it,
   * so the same goal always produces the same show.
   */
  start(rng: Rng, team: 0 | 1, goalSign: number): void {
    this.reset();
    this.rng = rng;

    const color = new Color(TEAM_COLORS[team]);
    this.shellColor[0] = color.r;
    this.shellColor[1] = color.g;
    this.shellColor[2] = color.b;
    this.peony = peonyRamp([color.r, color.g, color.b]);

    for (const plan of planFireworks(rng, goalSign, this.shellCount)) {
      this.shells.push({ plan, burst: false, trailCarry: 0 });
    }
    this.clock = 0;
    this.running = true;
  }

  update(dt: number): void {
    let alive = false;

    for (const g of this.glows) {
      if (g.life <= 0) continue;
      g.life = Math.max(0, g.life - dt);
      const k = g.life / TUNING.glowLife;
      g.mesh.visible = g.life > 0;
      g.mesh.scale.setScalar(TUNING.glowRadius * (1.6 - k * 0.9));
      g.material.opacity = 0.85 * k * k;
      if (g.life > 0) alive = true;
    }

    if (!this.running) {
      this.running = alive;
      return;
    }

    this.clock += dt;
    const rng = this.rng;

    for (const shell of this.shells) {
      if (shell.burst) continue;
      alive = true;

      const elapsed = this.clock - shell.plan.launch;
      if (elapsed < 0) continue;

      const u = elapsed / shell.plan.rise;
      if (u >= 1) {
        this.burst(shell.plan);
        shell.burst = true;
        continue;
      }

      if (!rng) continue;
      // Ease-out climb: a mortar decelerates all the way to the apex, and the
      // hang at the top is most of what sells the launch.
      const k = 1 - (1 - u) * (1 - u);
      const p = this.shellPosition(shell.plan, k, this.shellPoint);

      shell.trailCarry += TUNING.trailRate * dt;
      while (shell.trailCarry >= 1) {
        shell.trailCarry -= 1;
        this.particles.spawn({
          position: this.spawnPoint.set(
            p.x + rng.signed() * 8,
            p.y + rng.signed() * 8,
            p.z + rng.signed() * 8
          ),
          velocity: new Vector3(rng.signed() * 40, -60 - rng.next() * 90, rng.signed() * 40),
          life: rng.range(TUNING.trailLifeMin, TUNING.trailLifeMax),
          size: 46 + rng.next() * 52,
          sizeGrowth: 0.25,
          color: shell.plan.accent ? WHITE : this.shellColor,
          gravity: 180,
          drag: 2.6,
        });
      }
    }

    this.running = alive;
  }

  /** Wipe the display. Called on a kickoff, and before every new show. */
  reset(): void {
    this.shells.length = 0;
    this.clock = 0;
    this.running = false;
    this.rng = null;
    for (const g of this.glows) {
      g.life = 0;
      g.mesh.visible = false;
    }
  }

  dispose(): void {
    for (const g of this.glows) g.material.dispose();
    this.glowGeometry.dispose();
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private shellPosition(plan: ShellPlan, k: number, out: Vector3): Vector3 {
    return out.set(
      plan.fromX + (plan.apexX - plan.fromX) * k,
      plan.fromY + (plan.apexY - plan.fromY) * k,
      plan.fromZ + (plan.apexZ - plan.fromZ) * k
    );
  }

  private burst(plan: ShellPlan): void {
    const rng = this.rng;
    if (!rng) return;

    const origin = { x: plan.apexX, y: plan.apexY, z: plan.apexZ };
    const base = plan.accent ? GOLD : this.shellColor;
    const ramp = plan.kind === 'willow' ? willowRamp : plan.kind === 'peony' ? this.peony : null;

    for (const p of burstParticles(rng, plan.kind, origin, plan.scale)) {
      const color = p.tint === 1 ? WHITE : p.tint === 2 ? GOLD : base;
      this.particles.spawn({
        position: this.spawnPoint.set(p.px, p.py, p.pz),
        velocity: new Vector3(p.vx, p.vy, p.vz),
        life: p.life,
        size: p.size,
        sizeGrowth: p.sizeGrowth,
        color,
        // A crackle spark takes its own flicker phase; everything else takes the
        // burst's ramp, and a plain tint takes none at all.
        ramp: p.ramp >= 0 ? CRACKLE_RAMPS[p.ramp] : ramp ?? undefined,
        gravity: p.gravity,
        drag: p.drag,
      });
    }

    this.lightGlow(origin, base, plan.scale);
  }

  /** Take the dimmest glow slot, so a burst never goes unlit. */
  private lightGlow(
    origin: { x: number; y: number; z: number },
    color: readonly [number, number, number],
    scale: number
  ): void {
    let slot = this.glows[0];
    for (const g of this.glows) if (g.life < slot.life) slot = g;

    slot.life = TUNING.glowLife;
    slot.mesh.position.set(origin.x, origin.y, origin.z);
    slot.mesh.scale.setScalar(TUNING.glowRadius * scale);
    slot.mesh.visible = true;
    slot.material.color.setRGB(color[0], color[1], color[2]);
    slot.material.opacity = 0.85;
  }

  private makeGlow(): Glow {
    const material = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    const mesh = new Mesh(this.glowGeometry, material);
    mesh.visible = false;
    mesh.frustumCulled = false;
    this.group.add(mesh);
    return { mesh, material, life: 0 };
  }
}
